import { FormikWithRedirectFormHelpers, Id, useLaravelCrudEntity } from "@dgs/core";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { eventService } from "../../../shared/api/eventService";
import { userTenantService } from "../../../shared/api/userTenantService";
import { userGroupService } from "../shared/api/userGroupService";
import { userService } from "../shared/api/userService";
import { UserCreateFormValues, UserCreateRequest, UserFormValues } from "../shared/types/user";

const mapToCreateRequest = (values: UserCreateFormValues): UserCreateRequest => {
	if (!values.firstName || !values.lastName || !values.email) {
		throw new Error("Missing params");
	}
	return {
		firstName: values.firstName,
		lastName: values.lastName,
		email: values.email,
		company: values.company,
		position: values.position,
		tenantIds: values.tenants.map((t) => t.id),
	};
};

export const useUserForm = () => {
	const navigate = useNavigate();
	const { t } = useTranslation();
	const queryClient = useQueryClient();

	const { updateEntity, createEntity, deleteEntity } = useLaravelCrudEntity(userService, {
		messages: {
			create: t("User was successfully created."),
			delete: t("User was successfully deleted."),
			update: t("User was successfully updated."),
		},
		entityName: t("User"),
	});

	const resetUsers = useCallback(async () => {
		await queryClient.resetQueries({ queryKey: userGroupService.indexKey });
		await queryClient.resetQueries({ queryKey: userTenantService.allOptionsKey });
		await queryClient.resetQueries({ queryKey: eventService.indexKey });
		await queryClient.invalidateQueries({
			predicate: (query) => query.queryKey.includes("users"),
		});
	}, [queryClient]);

	const createUser = useCallback(
		async (values: UserCreateFormValues) => {
			const success = await createEntity(mapToCreateRequest(values));

			if (success) {
				void resetUsers();
				navigate(-1);
			}
		},
		[createEntity, navigate, resetUsers],
	);

	const updateUser = useCallback(
		async (userId: Id, { attributes }: UserFormValues, helpers: FormikWithRedirectFormHelpers<UserFormValues>) => {
			const body = Object.values(attributes)
				.filter((h) => h.type !== "PRIMARY_EMAIL")
				.map((h) => h.toRequestValue());

			const response = await updateEntity(userId, { attributes: body });

			if (response) {
				helpers.setTo(-1);
				void resetUsers();
				return true;
			}

			return false;
		},
		[resetUsers, updateEntity],
	);

	const deleteUser = useCallback(
		async (userId: string) => {
			const success = await deleteEntity(userId);

			if (success) {
				await queryClient.resetQueries({ queryKey: userGroupService.indexKey });
				await queryClient.resetQueries({ queryKey: userTenantService.allOptionsKey });
				await queryClient.resetQueries({ queryKey: eventService.indexKey });
				await queryClient.invalidateQueries({
					predicate: (query) =>
						query.queryKey.includes("users") &&
						!(query.queryKey.length === 1 && query.queryKey[0] === "users") &&
						!(query.queryKey.length === 2 && query.queryKey[1] === userId),
				});
				navigate(-1);
			}
		},
		[deleteEntity, navigate, queryClient],
	);

	return {
		createUser,
		updateUser,
		deleteUser,
	};
};
